import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiHome, FiFileText, FiFolder, FiPhone, FiUser,
  FiGrid, FiVideo, FiTool, FiBell,
  FiImage, FiCode, FiLogIn, FiShoppingBag,
  FiMoreHorizontal, FiX, FiSun, FiMoon, FiChevronRight,
} from "react-icons/fi";
import { auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useTheme } from "./Dark";
import "./Navbar.css";

/* ═══════════════════════════════════════════════════════════
   NAVBAR — floating bottom dock + full-page sheet
   Same logic as before (routes, hide-on-scroll, theme, admin
   link, 5/4 visible items + More). Only the surface changed.
   ═══════════════════════════════════════════════════════════ */

export default function Navbar({ children }) {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [active, setActive] = useState(location.pathname);
  const [hideNav, setHideNav] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  // Track user
  useEffect(() => {
    if (!auth) return; // preview without Firebase — no user
    const unsub = onAuthStateChanged(auth, (user) => setCurrentUser(user));
    return () => unsub();
  }, []);

  // Sync active route
  useEffect(() => {
    setActive(location.pathname);
    setShowMore(false); // Close menu on route change
  }, [location.pathname]);

  // Detect mobile
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Hide on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > lastScrollY && window.scrollY > 100) {
        setHideNav(true);
        setShowMore(false);
      } else {
        setHideNav(false);
      }
      setLastScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  const isAdmin = currentUser?.email === "karanksxxx@gmail.com";

  // All portfolio navigation links
  const navItems = [
    { path: "/", label: "Home", icon: <FiHome /> },
    { path: "/About", label: "About", icon: <FiUser /> },
    { path: "/Projects", label: "Projects", icon: <FiFolder /> },
    { path: "/Blogs", label: "Blogs", icon: <FiFileText /> },
    { path: "/Contact", label: "Contact", icon: <FiPhone /> },
    { path: "/Services", label: "Services", icon: <FiTool /> },
    { path: "/products", label: "Products", icon: <FiShoppingBag /> },
    { path: "/Gallery", label: "Gallery", icon: <FiImage /> },
    { path: "/Note", label: "Notes", icon: <FiGrid /> },
    { path: "/codeupload", label: "Codes", icon: <FiCode /> },
    { path: "/Video", label: "Videos", icon: <FiVideo /> },
    { path: "/Login", label: "Login", icon: <FiLogIn /> },
    ...(isAdmin
      ? [{ path: "/real-time-dashboard", label: "Notify", icon: <FiBell /> }]
      : []),
  ];

  // Desktop bottom bar: 5 visible items; mobile: 4
  const visibleCount = isMobile ? 4 : 5;
  const visibleItems = navItems.slice(0, visibleCount);

  // Animated Sun/Moon icon shared by all toggles
  const themeIcon = (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={theme}
        className="kb-theme__glyph"
        initial={{ rotate: -120, opacity: 0, scale: 0.5 }}
        animate={{ rotate: 0, opacity: 1, scale: 1 }}
        exit={{ rotate: 120, opacity: 0, scale: 0.5 }}
        transition={{ duration: 0.3 }}
      >
        {theme === "dark" ? <FiSun /> : <FiMoon />}
      </motion.span>
    </AnimatePresence>
  );

  return (
    <>
      <div className="navbar-content">{children}</div>

      {/* In-flow spacer: keeps page ends clear of the fixed dock */}
      <div className="nav-spacer" aria-hidden="true" />

      {/* ═══════════ EXPAND SHEET (ALL NAVLINKS) ═══════════ */}
      <AnimatePresence>
        {showMore && (
          <>
            <motion.div
              className="kb-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMore(false)}
            />

            <motion.nav
              className="kb-sheet"
              aria-label="All pages"
              initial={{ opacity: 0, y: 26, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 26, scale: 0.97 }}
              transition={{ type: "spring", damping: 26, stiffness: 300 }}
            >
              <div className="kb-sheet__head">
                <div className="kb-sheet__title">
                  <span className="kb-sheet__eyebrow"><i /> All pages</span>
                  <b>{navItems.length} pages</b>
                </div>

                <div className="kb-sheet__actions">
                  <motion.button
                    className="kb-sheet__btn"
                    onClick={toggleTheme}
                    whileHover={{ rotate: 20 }}
                    whileTap={{ scale: 0.9 }}
                    title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
                    aria-label="Toggle theme"
                  >
                    {themeIcon}
                  </motion.button>
                  <motion.button
                    className="kb-sheet__btn kb-sheet__close"
                    onClick={() => setShowMore(false)}
                    whileHover={{ rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label="Close menu"
                  >
                    <FiX />
                  </motion.button>
                </div>
              </div>

              <div className="kb-grid">
                {navItems.map((item, i) => {
                  const isActive = active === item.path;
                  return (
                    <motion.div
                      key={item.path}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(i * 0.02, 0.25) }}
                    >
                      <Link
                        to={item.path}
                        className={`kb-link ${isActive ? "is-active" : ""}`}
                        onClick={() => {
                          setActive(item.path);
                          setShowMore(false);
                        }}
                      >
                        <span className="kb-link__ic">{item.icon}</span>
                        <span className="kb-link__info">
                          <b>{item.label}</b>
                          <em>{item.path === "/" ? "Home page" : item.path}</em>
                        </span>
                        {isActive ? (
                          <span className="kb-link__badge">Active</span>
                        ) : (
                          <FiChevronRight className="kb-link__arrow" />
                        )}
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      {/* ═══════════ DESKTOP DOCK ═══════════ */}
      {!isMobile && (
        <motion.div
          className="kb-dock"
          initial={{ y: 110 }}
          animate={{ y: hideNav ? 110 : 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          {visibleItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`kb-item ${active === item.path ? "is-active" : ""}`}
              onClick={() => setActive(item.path)}
            >
              <span className="kb-item__ic">{item.icon}</span>
              <span className="kb-item__label">{item.label}</span>
              {active === item.path && (
                <motion.span
                  className="kb-ind"
                  layoutId="kbDesktopIndicator"
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                />
              )}
            </Link>
          ))}

          <div
            className={`kb-item kb-more ${showMore ? "is-active" : ""}`}
            onClick={() => setShowMore(!showMore)}
            role="button"
            tabIndex={0}
            title="All pages"
            aria-label="All pages"
          >
            <span className="kb-item__ic">
              <motion.span
                className="kb-rot"
                animate={{ rotate: showMore ? 45 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {showMore ? <FiX /> : <FiMoreHorizontal />}
              </motion.span>
            </span>
            <span className="kb-item__label">More</span>
            {showMore && (
              <motion.span
                className="kb-ind"
                layoutId="kbDesktopIndicator"
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
              />
            )}
          </div>

          <span className="kb-div" aria-hidden="true" />

         
        </motion.div>
      )}

      {/* ═══════════ MOBILE DOCK ═══════════ */}
      {isMobile && (
        <motion.div
          className="kb-dock kb-dock--mobile"
          initial={{ y: 110 }}
          animate={{ y: hideNav ? 110 : 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          {visibleItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`kb-item ${active === item.path ? "is-active" : ""}`}
              onClick={() => setActive(item.path)}
            >
              <span className="kb-item__ic">
                {item.icon}
                {active === item.path && (
                  <motion.span
                    className="kb-dot"
                    layoutId="kbMobileDot"
                    transition={{ type: "spring", damping: 20, stiffness: 300 }}
                  />
                )}
              </span>
              <span className="kb-item__label">{item.label}</span>
            </Link>
          ))}

          <div
            className={`kb-item kb-more ${showMore ? "is-active" : ""}`}
            onClick={() => setShowMore(!showMore)}
            role="button"
            tabIndex={0}
            title="All pages"
            aria-label="All pages"
          >
            <span className="kb-item__ic">
              <motion.span
                className="kb-rot"
                animate={{ rotate: showMore ? 45 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {showMore ? <FiX /> : <FiMoreHorizontal />}
              </motion.span>
              {showMore && (
                <motion.span
                  className="kb-dot"
                  layoutId="kbMobileDot"
                  transition={{ type: "spring", damping: 20, stiffness: 300 }}
                />
              )}
            </span>
            <span className="kb-item__label">More</span>
          </div>

          
        </motion.div>
      )}
    </>
  );
}